import { get } from 'lodash';
import { action, computed, makeObservable, observable } from 'mobx';
import xss from 'xss';

import API from 'app/API';
import Cacheable from 'app/store/Cacheable';
import { isOkResponse } from 'queries/utils';
import { isDefined } from 'utils/is-defined';
import { mdToPlain } from 'utils/md-to-plain';
import Utils from 'utils/Utils';

// TODO: add explicit typings

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BulletinData = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BulletinReferences = Record<string, any>;
type ValueOf<T> = T[keyof T];

export default class BulletinStore extends Cacheable {
    public static ERRORS = {
        NOT_FOUND: 'NOT_FOUND'
    } as const;

    @observable error: ValueOf<typeof BulletinStore.ERRORS> | null = null;
    @observable data: BulletinData = {};
    @observable references: BulletinReferences = {};
    @observable isLoading = false;

    controller: AbortController | null = null;

    constructor() {
        // TODO: [mobx-undecorate] verify the constructor arguments and the arguments of this automatically generated super call
        super();

        makeObservable(this);
    }

    consume(cachedEntry) {
        if (cachedEntry.data) {
            this.data = cachedEntry.data;
        }
        if (cachedEntry.references) {
            this.references = cachedEntry.references;
        }
        if (cachedEntry.error) {
            this.error = cachedEntry.error;
        }
    }

    flattenReferences(options: { id: string; references: BulletinReferences }) {
        return Object.entries(options.references).reduce((output, [bulletinFamily, value]) => {
            const items = value
                .filter((item) => item.id !== options.id)
                .map((item) => ({
                    id: item.id,
                    type: item.type,
                    title: item.title,
                    published: item.published,
                    bulletinFamily
                }));

            if (items.length > 0) {
                output[bulletinFamily] = items;
            }

            return output;
        }, {});
    }

    @action loadItem = async ({ id }: { id: string }) => {
        const request = API.SEARCH_ID;
        const queryKey = JSON.stringify([request.url, id]);

        if (this.hasItemInCache(queryKey)) {
            return this.consume(this.getItemFromCache(queryKey));
        }

        this.controller = new AbortController();
        this.isLoading = true;
        try {
            const response = await API.fetch(
                API.SEARCH_ID,
                {
                    id,
                    references: 'True',
                    referenceFields: ['id', 'type', 'bulletinFamily', 'title', 'published']
                },
                null,
                null,
                null,
                this.controller.signal
            );
            if (isOkResponse(response)) {
                const data = get(response, ['data', 'documents', id], {});
                const references = get(response, ['data', 'references', id], {});

                if (isDefined(data)) {
                    this.data = data;
                    this.references = this.flattenReferences({ id, references });
                    this.error = null;
                } else {
                    this.data = {};
                    this.references = {};
                    this.error = BulletinStore.ERRORS.NOT_FOUND;
                }
            } else {
                this.data = {};
                this.references = {};
                const errorCode = get(response, ['data', 'errorCode']);
                switch (errorCode) {
                    default:
                        this.error = BulletinStore.ERRORS.NOT_FOUND;
                        break;
                }
            }

            this.setItemToCache(queryKey, { data: this.data, references: this.references, error: this.error });
        } finally {
            this.isLoading = false;
        }
    };

    @computed
    get shortTitle() {
        if (this.data.id) {
            const { id, title, bulletinFamily } = this.data;
            const brief = title || id;

            const postfix =
                bulletinFamily === 'exploit'
                    ? ' - exploit database | Vulners.com'
                    : ' - vulnerability database | Vulners.com';
            const shortTitle = Utils.trunc(
                100 - postfix.length,
                xss(`${brief}`)
                    .replace(/[\r\n\\]/g, '')
                    .trim()
            );
            return shortTitle + postfix;
        } else {
            return '';
        }
    }

    @computed
    get shortDescription() {
        const fullDescription = this.data.aiDescription?.trim() || this.data.description?.trim();
        if (fullDescription) {
            return Utils.trunc(
                250,
                mdToPlain(fullDescription)
                    .replace(/[\r\n\\]/g, '')
                    .trim()
            );
        }
        return '';
    }
}
